import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { Leaf, ShieldAlert, Sprout, Cpu, Bike, ShieldCheck, Wifi, Accessibility, Building2, Armchair } from "lucide-react";

const ICONS = [Leaf, ShieldAlert, Sprout, Cpu, Bike, ShieldCheck, Wifi, Accessibility, Building2, Armchair];

export default function Impact() {
  const { t } = useLang();
  const c = t.impact;
  return (
    <section
      id="impact"
      data-testid="section-impact"
      className="relative bg-[#16191D] py-32 md:py-44"
    >
      {/* subtle grid backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-14">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8">
                <span className="h-px w-8 bg-[#F5D77A]" />
                {c.kicker}
              </div>
              <h2
                className="font-display font-light tracking-[-0.03em] leading-[1.02] text-white"
                style={{ fontSize: "clamp(2.5rem, 6.5vw, 6rem)" }}
              >
                {c.title}
              </h2>
            </motion.div>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="text-white/55 text-base leading-relaxed"
            >
              {c.sub}
            </motion.p>
          </div>
        </div>

        {/* 11 goals — impact matrix */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/8 border border-white/8">
          {c.items.map((label, i) => {
            const Icon = ICONS[i] || ShieldCheck;
            return (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`impact-cell-${i}`}
                className="group relative bg-[#16191D] hover:bg-[#1F2328] transition-colors min-h-[220px] md:min-h-[260px] p-7 md:p-9 flex flex-col justify-between"
              >
                <div className="flex items-start justify-between">
                  <Icon
                    className="h-6 w-6 text-[#F5D77A] group-hover:scale-110 transition-transform"
                    strokeWidth={1.25}
                  />
                  <span className="text-[10px] tracking-[0.35em] text-white/25 font-display">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div>
                  <h3
                    className="font-display font-light tracking-[-0.02em] leading-[1.05] text-white"
                    style={{ fontSize: "clamp(1.1rem, 1.5vw, 1.6rem)" }}
                  >
                    {label}
                  </h3>
                  <div className="mt-4 h-px w-6 bg-[#F5D77A]/50 group-hover:w-14 transition-all duration-500" />
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-8 text-[13px] text-white/40 italic leading-relaxed border-l-2 border-[#F5D77A]/30 pl-5 max-w-2xl"
        >
          {c.note}
        </motion.p>
      </div>
    </section>
  );
}
